/**
 * Differential heatmap generation utilities
 *
 * Generates a heatmap showing the DIFFERENCE between two groups:
 * - Red = Group A looks more
 * - White/Neutral = Similar attention
 * - Blue = Group B looks more
 *
 * Useful for comparing gaze patterns between two conditions or user groups.
 */

import type {
  UVCoordinates,
  DifferentialHeatmapConfig,
  DifferentialHeatmapResult,
  DifferentialColorScheme,
  DifferentialNormalizationMethod,
} from "../types/gazeHeatmapTypes";

/**
 * Divergent color gradient definitions
 * Each gradient goes from negative (Group B > A) to positive (Group A > B)
 * Position: -1 to +1
 */
const DIVERGENT_GRADIENTS: Record<
  DifferentialColorScheme,
  [number, number, number, number, number][]
> = {
  // Red-White-Blue (classic divergent)
  redBlue: [
    [-1.0, 33, 102, 172, 0.9], // Strong blue (Group B dominates)
    [-0.5, 103, 169, 207, 0.7], // Light blue
    [-0.1, 209, 229, 240, 0.3], // Very light blue
    [0.0, 255, 255, 255, 0.0], // White (neutral / transparent)
    [0.1, 253, 219, 199, 0.3], // Very light red
    [0.5, 239, 138, 98, 0.7], // Light red
    [1.0, 178, 24, 43, 0.9], // Strong red (Group A dominates)
  ],
  // Red-Gray-Blue (neutral center is more visible)
  redGray: [
    [-1.0, 33, 102, 172, 0.9], // Strong blue
    [-0.5, 103, 169, 207, 0.7], // Light blue
    [-0.1, 180, 180, 190, 0.3], // Light gray-blue
    [0.0, 200, 200, 200, 0.2], // Gray (neutral)
    [0.1, 190, 180, 180, 0.3], // Light gray-red
    [0.5, 239, 138, 98, 0.7], // Light red
    [1.0, 178, 24, 43, 0.9], // Strong red
  ],
  // Purple-White-Green (colorblind-friendly alternative)
  purpleGreen: [
    [-1.0, 64, 0, 75, 0.9], // Strong purple (Group B)
    [-0.5, 153, 112, 171, 0.7], // Light purple
    [-0.1, 216, 179, 219, 0.3], // Very light purple
    [0.0, 255, 255, 255, 0.0], // White (neutral)
    [0.1, 194, 228, 197, 0.3], // Very light green
    [0.5, 90, 174, 97, 0.7], // Light green
    [1.0, 27, 120, 55, 0.9], // Strong green (Group A)
  ],
};

/**
 * Interpolate color from a divergent gradient
 *
 * @param gradient - Divergent gradient definition
 * @param t - Position from -1 (Group B) to +1 (Group A)
 * @returns RGBA values
 */
function interpolateDivergentColor(
  gradient: [number, number, number, number, number][],
  t: number,
): [number, number, number, number] {
  // Clamp to [-1, 1]
  t = Math.max(-1, Math.min(1, t));

  // Find the two gradient stops to interpolate between
  let lowerStop = gradient[0];
  let upperStop = gradient[gradient.length - 1];

  for (let i = 0; i < gradient.length - 1; i++) {
    if (t >= gradient[i][0] && t <= gradient[i + 1][0]) {
      lowerStop = gradient[i];
      upperStop = gradient[i + 1];
      break;
    }
  }

  // Calculate interpolation factor
  const range = upperStop[0] - lowerStop[0];
  const factor = range > 0 ? (t - lowerStop[0]) / range : 0.5;

  // Interpolate each channel
  const r = Math.round(lowerStop[1] + factor * (upperStop[1] - lowerStop[1]));
  const g = Math.round(lowerStop[2] + factor * (upperStop[2] - lowerStop[2]));
  const b = Math.round(lowerStop[3] + factor * (upperStop[3] - lowerStop[3]));
  const a = Math.round((lowerStop[4] + factor * (upperStop[4] - lowerStop[4])) * 255);

  return [r, g, b, a];
}

/**
 * Create a Gaussian kernel for density estimation
 */
function createGaussianKernel(radius: number, sigma: number): number[][] {
  const size = radius * 2 + 1;
  const kernel: number[][] = [];
  const sigma2 = 2 * sigma * sigma;

  for (let y = 0; y < size; y++) {
    kernel[y] = [];
    for (let x = 0; x < size; x++) {
      const dx = x - radius;
      const dy = y - radius;
      const distance2 = dx * dx + dy * dy;
      kernel[y][x] = Math.exp(-distance2 / sigma2);
    }
  }

  return kernel;
}

/**
 * Generate density grid from UV points using KDE
 */
function generateDensityGrid(
  points: UVCoordinates[],
  width: number,
  height: number,
  kernel: number[][],
  kernelRadius: number,
): { density: Float32Array; maxDensity: number; meanDensity: number } {
  const density = new Float32Array(width * height);
  const kernelSize = kernelRadius * 2 + 1;

  // Apply kernel to each point
  for (const point of points) {
    const centerX = Math.floor(point.u * width);
    const centerY = Math.floor((1 - point.v) * height);

    for (let ky = 0; ky < kernelSize; ky++) {
      const py = centerY + ky - kernelRadius;
      if (py < 0 || py >= height) continue;

      for (let kx = 0; kx < kernelSize; kx++) {
        // Handle horizontal wrap-around
        let px = centerX + kx - kernelRadius;
        if (px < 0) px += width;
        if (px >= width) px -= width;

        const idx = py * width + px;
        density[idx] += kernel[ky][kx];
      }
    }
  }

  // Calculate stats
  let maxDensity = 0;
  let sumDensity = 0;
  for (let i = 0; i < density.length; i++) {
    if (density[i] > maxDensity) maxDensity = density[i];
    sumDensity += density[i];
  }

  return {
    density,
    maxDensity,
    meanDensity: sumDensity / density.length,
  };
}

/**
 * Normalize density grid based on method
 */
function normalizeDensity(
  density: Float32Array,
  pointCount: number,
  maxDensity: number,
  method: DifferentialNormalizationMethod,
): Float32Array {
  const normalized = new Float32Array(density.length);

  switch (method) {
    case "perSample":
      // Divide by number of points to make comparable
      // This is the most intuitive for comparing groups of different sizes
      if (pointCount > 0) {
        for (let i = 0; i < density.length; i++) {
          normalized[i] = density[i] / pointCount;
        }
      }
      break;

    case "maxDensity":
      // Normalize to [0, 1] based on max density
      if (maxDensity > 0) {
        for (let i = 0; i < density.length; i++) {
          normalized[i] = density[i] / maxDensity;
        }
      }
      break;

    case "zscore": {
      // Z-score normalization for statistical comparison
      let mean = 0;
      for (let i = 0; i < density.length; i++) {
        mean += density[i];
      }
      mean /= density.length;

      let variance = 0;
      for (let i = 0; i < density.length; i++) {
        const diff = density[i] - mean;
        variance += diff * diff;
      }
      variance /= density.length;
      const stdDev = Math.sqrt(variance);

      if (stdDev > 0) {
        for (let i = 0; i < density.length; i++) {
          normalized[i] = (density[i] - mean) / stdDev;
        }
      }
      break;
    }
  }

  return normalized;
}

/**
 * Generate a differential heatmap comparing two groups
 *
 * @param pointsA - UV coordinates for Group A
 * @param pointsB - UV coordinates for Group B
 * @param config - Generation configuration
 * @returns Differential heatmap result
 */
export function generateDifferentialHeatmap(
  pointsA: UVCoordinates[],
  pointsB: UVCoordinates[],
  config: Partial<DifferentialHeatmapConfig> = {},
): DifferentialHeatmapResult {
  const startTime = performance.now();

  // Merge with defaults
  const fullConfig: DifferentialHeatmapConfig = {
    width: config.width ?? 2048,
    height: config.height ?? 1024,
    kernelRadius: config.kernelRadius ?? 30,
    kernelSigma: config.kernelSigma ?? 15,
    colorScheme: config.colorScheme ?? "redBlue",
    normalization: config.normalization ?? "perSample",
    contrastFactor: config.contrastFactor ?? 1.0,
  };

  const { width, height, kernelRadius, kernelSigma, colorScheme, normalization, contrastFactor } =
    fullConfig;

  // Create shared kernel
  const kernel = createGaussianKernel(kernelRadius, kernelSigma);

  // Generate density grids for both groups
  const gridA = generateDensityGrid(pointsA, width, height, kernel, kernelRadius);
  const gridB = generateDensityGrid(pointsB, width, height, kernel, kernelRadius);

  // Normalize both grids
  const normalizedA = normalizeDensity(
    gridA.density,
    pointsA.length,
    gridA.maxDensity,
    normalization,
  );
  const normalizedB = normalizeDensity(
    gridB.density,
    pointsB.length,
    gridB.maxDensity,
    normalization,
  );

  // Calculate difference: A - B
  // Positive = Group A has more attention
  // Negative = Group B has more attention
  const difference = new Float32Array(width * height);
  let maxPositive = 0;
  let maxNegative = 0;

  for (let i = 0; i < difference.length; i++) {
    const diff = normalizedA[i] - normalizedB[i];
    difference[i] = diff;

    if (diff > maxPositive) maxPositive = diff;
    if (diff < maxNegative) maxNegative = diff;
  }

  // Find maximum absolute difference for normalization
  const maxAbsDiff = Math.max(Math.abs(maxPositive), Math.abs(maxNegative));

  // Create ImageData
  const imageData = new ImageData(width, height);
  const data = imageData.data;
  const gradient = DIVERGENT_GRADIENTS[colorScheme];

  // Convert difference to colors
  for (let i = 0; i < difference.length; i++) {
    // Normalize to [-1, 1] and apply contrast
    let normalizedDiff = 0;
    if (maxAbsDiff > 0) {
      normalizedDiff = (difference[i] / maxAbsDiff) * contrastFactor;
      normalizedDiff = Math.max(-1, Math.min(1, normalizedDiff));
    }

    const [r, g, b, a] = interpolateDivergentColor(gradient, normalizedDiff);

    const pixelIdx = i * 4;
    data[pixelIdx] = r;
    data[pixelIdx + 1] = g;
    data[pixelIdx + 2] = b;
    data[pixelIdx + 3] = a;
  }

  const processingTime = performance.now() - startTime;

  return {
    imageData,
    maxPositive,
    maxNegative,
    groupAStats: {
      pointCount: pointsA.length,
      maxDensity: gridA.maxDensity,
      meanDensity: gridA.meanDensity,
    },
    groupBStats: {
      pointCount: pointsB.length,
      maxDensity: gridB.maxDensity,
      meanDensity: gridB.meanDensity,
    },
    processingTime,
  };
}

/**
 * Generate a legend image for the differential heatmap
 *
 * @param colorScheme - Color scheme to use
 * @param width - Legend width in pixels
 * @param height - Legend height in pixels
 * @returns ImageData for the legend
 */
export function generateDifferentialLegendImage(
  colorScheme: DifferentialColorScheme,
  width = 200,
  height = 20,
): ImageData {
  const imageData = new ImageData(width, height);
  const data = imageData.data;
  const gradient = DIVERGENT_GRADIENTS[colorScheme];

  for (let x = 0; x < width; x++) {
    // Map x to [-1, 1]
    const t = (x / (width - 1)) * 2 - 1;
    const [r, g, b, a] = interpolateDivergentColor(gradient, t);

    for (let y = 0; y < height; y++) {
      const idx = (y * width + x) * 4;
      data[idx] = r;
      data[idx + 1] = g;
      data[idx + 2] = b;
      data[idx + 3] = a;
    }
  }

  return imageData;
}
